import { orderXClient } from '@/service/client'
import {
  PortfolioLineChart,
  PortfolioLineChartRequest,
  ServerMessage,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export async function fetchPortfolioLineChart(
  params: PortfolioLineChartRequest
): Promise<PortfolioLineChart> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      portfolioLineChart: params,
    })

    if (!data.data?.portfolioLineChart) {
      // A response without the payload is a transient server condition, not an
      // empty history (that arrives as a populated message with empty values).
      // Throw so React Query keeps the previously fetched series instead of
      // replacing it with an empty one, which would flip the account-cards
      // headline onto the WS-derived balance until the next successful poll.
      throw new Error('portfolioLineChart missing from server response')
    }

    return data.data.portfolioLineChart
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

/**
 * Multi-account variant — `params.accountIds` populated (and `accountId: ''`)
 * gets `PortfolioLineChartList { charts: PortfolioLineChart[] }` back instead
 * of the singular `portfolioLineChart` field (see the proto doc comment on
 * `PortfolioLineChartList`: "chosen by the request's shape, never by how many
 * accounts resolved"). Series are NOT pre-summed by the backend — the caller
 * aggregates them (see portfolio-chart-aggregate.ts).
 *
 * NOTE: the exact response shape here (does `charts[]` actually come back
 * populated with per-account `ticks`/`values`? does a single request work
 * across accounts on different trade venues, given `trade_venue` is still a
 * required singular field on the request?) has not yet been verified against
 * a live backend — see the "All Accounts" aggregation plan's step 4.
 */
export async function fetchPortfolioLineChartList(
  params: PortfolioLineChartRequest
): Promise<PortfolioLineChart[]> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      portfolioLineChart: params,
    })

    if (!data.data?.portfolioLineChartList) {
      throw new Error('portfolioLineChartList missing from server response')
    }

    return data.data.portfolioLineChartList.charts
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}
